import { UavFlightDto, UavFlightsQuery } from '@app/services/uav-flights.service';
import {
  GeneralGisBounds,
  resolveUavFlightsExtent,
  uavCoverageBounds,
} from '@app/pages/general-map/general-gis/utils/general-gis-uav-flights.util';

/**
 * Margin, in degrees, added around the survey's own points before the flights are looked up. A survey is stored as
 * a point (the raster's north-west corner), which has no area, and the flights query needs a real extent.
 */
const PROBE_MARGIN_DEGREES = 0.0005;
/** `resolveUavFlightsExtent` needs a zoom; the probe is not a viewport, so the value does not matter. */
const PROBE_ZOOM = 0;

/**
 * The `GetAvailableDates` query that finds the orthophoto of a survey from the points the survey is drawn with.
 * `null` when the points do not give a valid extent, including an extent wider than the backend accepts
 * (`UAV_FLIGHTS_MAX_EXTENT_DEGREES`); the caller then keeps the view on the points.
 */
export function buildUavSurveyProbeQuery(points: GeneralGisBounds): UavFlightsQuery | null {
  const result = resolveUavFlightsExtent({
    west: points.west - PROBE_MARGIN_DEGREES,
    east: points.east + PROBE_MARGIN_DEGREES,
    south: points.south - PROBE_MARGIN_DEGREES,
    north: points.north + PROBE_MARGIN_DEGREES,
    zoom: PROBE_ZOOM,
  });
  return result.kind === 'ok' ? { ...result.extent } : null;
}

/** The geographic bounds of the orthophoto of the given survey, or `null` when it is not among the flights. */
export function findUavSurveyCoverageBounds(
  flights: readonly UavFlightDto[],
  surveyId: string,
): GeneralGisBounds | null {
  const flight = flights.find((candidate) => candidate.flightId === surveyId);
  return flight ? uavCoverageBounds(flight) : null;
}

/**
 * What the flights lookup says about a survey's orthophoto: `found` with its bounds, `absent` when the lookup worked
 * and the survey has no ready orthophoto, `unknown` when that could not be established.
 */
export type UavSurveyCoverageLookup =
  | { readonly kind: 'found'; readonly bounds: GeneralGisBounds }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unknown' };

export const UAV_COVERAGE_UNKNOWN: UavSurveyCoverageLookup = { kind: 'unknown' };

/** Classifies the answer of the flights lookup for one survey. */
export function lookupUavSurveyCoverage(
  flights: readonly UavFlightDto[],
  surveyId: string,
): UavSurveyCoverageLookup {
  if (!flights.some((candidate) => candidate.flightId === surveyId)) {
    return { kind: 'absent' };
  }
  const bounds = findUavSurveyCoverageBounds(flights, surveyId);
  return bounds ? { kind: 'found', bounds } : UAV_COVERAGE_UNKNOWN;
}
